// ABOUTME: Renders a button on model-selection product cards opening the product detail page in a modal.
// ABOUTME: The modal shows the model-selection-pdp detail layout and provides the card's prototype to its Configure action.

import type { IProduct } from '@coolshop/cool-model-selection';
import {
    Alert,
    ArticleOutlinedIcon,
    Box,
    CircularProgress,
    CloseIcon,
    Dialog,
    DialogContent,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from '@ds/coolshop';
import { CustomPageDetailView } from '@src/_new/modules/custom-pages/components/CustomPageDetailView';
import { CustomPageOriginContext } from '@src/_new/modules/custom-pages/context/CustomPageOriginContext';
import { useCustomPages } from '@src/_new/modules/custom-pages/hooks/useCustomPages';
import { CustomPage } from '@src/_new/modules/custom-pages/types';
import { CatalogConfiguratorHost } from '@src/_new/modules/custom-pages/widgets/components/CatalogConfiguratorHost';
import { useCatalogConfiguratorAction } from '@src/_new/modules/custom-pages/widgets/hooks/useCatalogConfiguratorAction';
import { useConfiguratorSessionEnd } from '@src/_new/modules/custom-pages/widgets/hooks/useConfiguratorSessionEnd';
import { readFirstLinkedNodeId } from '@src/_new/modules/custom-pages/widgets/utils/readFirstLinkedNodeId';
import { useGetNodeQuery } from '@src/components/hierarchies/Hierarchy.generated';
import { SettingsContext } from '@src/modules/setting/SettingsContext';
import { ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { resolveProductFavoriteTarget } from '../../utils/resolveProductFavoriteTarget';

// The custom page whose detail layout is the product detail page of the catalog.
export const PRODUCT_DETAIL_PAGE_SLUG = 'model-selection-pdp';

type ProductWithConfigurable = IProduct & {
    isConfigurable?: boolean;
};

// The link content of a prototype holding the id of the model node it was built from.
const PROTOTYPE_MODEL_CONTENT_TYPE = 'model';

interface PrototypeModelDetailPageContentProps {
    page: CustomPage;
    prototypeHierarchyId: string;
    prototypeNodeId: string;
}

/**
 * The detail page of the model a prototype was built from, for a page on the model hierarchy: the
 * catalog lists the configurable products as prototypes, so their model is read from the
 * prototype's `model` link. Mounted only while the dialog is open, so the cards issue no request.
 */
function PrototypeModelDetailPageContent({
    page,
    prototypeHierarchyId,
    prototypeNodeId,
}: PrototypeModelDetailPageContentProps) {
    const { formatMessage } = useIntl();
    const { data, loading, error } = useGetNodeQuery({
        variables: { hierarchiesId: prototypeHierarchyId, nodeFilter: { ids: [prototypeNodeId] } },
    });

    if (loading) {
        return (
            <Stack alignItems="center" padding={4}>
                <CircularProgress size="medium" />
            </Stack>
        );
    }

    const modelNodeId = readFirstLinkedNodeId(
        data?.hierarchies?.[0]?.nodes?.[0]?.contents,
        PROTOTYPE_MODEL_CONTENT_TYPE,
    );

    if (error || !modelNodeId) {
        return (
            <Box padding={2}>
                <Alert severity="warning">
                    {formatMessage({
                        id: 'catalog.productDetail.notFound',
                        defaultMessage: 'No product details are available for this product.',
                    })}
                </Alert>
            </Box>
        );
    }

    return <CustomPageDetailView page={page} nodeId={modelNodeId} />;
}

interface ProductDetailSessionProps {
    dialogOpen: boolean;
    prototypeId?: string;
    onCloseDialog: () => void;
    onConfigure: () => void;
    onConfiguratorClosed: () => void;
    /** The dialog content (title row and detail view). */
    children: ReactNode;
}

/**
 * The PDP dialog plus the configurator it can open. The configurator lives outside the dialog and
 * outlives it: Configure closes the dialog first, so the drawer never sits under it.
 */
function ProductDetailSession({
    dialogOpen,
    prototypeId,
    onCloseDialog,
    onConfigure,
    onConfiguratorClosed,
    children,
}: ProductDetailSessionProps) {
    const action = useCatalogConfiguratorAction();
    // Closing the drawer starts the save, so the session stays mounted (with the host's saving
    // backdrop) until the save is over too.
    useConfiguratorSessionEnd({
        open: !!action.chainedFlow.session,
        saving: action.isSaving,
        onEnd: onConfiguratorClosed,
    });
    const { openConfigurator } = action;
    const origin = useMemo(
        () => ({
            prototypeId,
            embedded: true,
            // The page's back button closes the dialog, back to the catalog it was opened from.
            onBack: onCloseDialog,
            openConfigurator: (id: string) => {
                onConfigure();
                openConfigurator(id);
            },
        }),
        [prototypeId, onCloseDialog, onConfigure, openConfigurator],
    );
    return (
        <CustomPageOriginContext.Provider value={origin}>
            {dialogOpen && (
                <Dialog
                    open
                    onClose={onCloseDialog}
                    maxWidth="lg"
                    fullWidth
                    disablePaddings
                    data-testid="product-detail-dialog"
                >
                    <DialogContent>{children}</DialogContent>
                </Dialog>
            )}
            <CatalogConfiguratorHost action={action} enableFavoriteAction />
        </CustomPageOriginContext.Provider>
    );
}

/**
 * Start-action slot for the model-selection product card. Renders nothing when the tenant has no
 * `model-selection-pdp` custom page the user can access, or when the card cannot be brought to a
 * node of that page's hierarchy (the detail layout only knows the nodes of its own hierarchy): a
 * card of the page hierarchy opens as is, a prototype card on a model page opens its model.
 */
export function ProductDetailPageAction(product: IProduct) {
    const { formatMessage } = useIntl();
    const settings = useContext(SettingsContext);
    const customPages = useCustomPages();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [configuring, setConfiguring] = useState(false);

    const closeDialog = useCallback(() => setDialogOpen(false), []);
    const startConfiguring = useCallback(() => {
        setDialogOpen(false);
        setConfiguring(true);
    }, []);
    const stopConfiguring = useCallback(() => setConfiguring(false), []);

    const page = useMemo(
        () => customPages.find(customPage => customPage.slug === PRODUCT_DETAIL_PAGE_SLUG),
        [customPages],
    );

    const { id, isConfigurable, description } = product as ProductWithConfigurable;
    const target = resolveProductFavoriteTarget(
        { id, isConfigurable },
        {
            model: settings.prototype?.hierarchies?.model,
            prototype: settings.prototype?.hierarchies?.prototype,
        },
    );

    const modelHierarchyId = settings.prototype?.hierarchies?.model;
    const prototypeHierarchyId = settings.prototype?.hierarchies?.prototype;
    const isPageNode = !!page && !!target && target.hierarchyId === page.hierarchyId;
    const isPrototypeOfModelPage =
        !!page &&
        !!target &&
        !!modelHierarchyId &&
        page.hierarchyId === modelHierarchyId &&
        target.hierarchyId === prototypeHierarchyId;

    if (!page || !target || (!isPageNode && !isPrototypeOfModelPage)) {
        return null;
    }

    // The card itself is clickable (it opens the configurator), so neither the trigger nor the
    // dialog may let their clicks bubble up to it.
    const stopAndRun = (run: () => void) => (event: React.MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        run();
    };

    const closeLabel = formatMessage({ id: 'close', defaultMessage: 'Close' });

    return (
        // Only the bubbling is stopped here: the default action is left alone so that the links
        // and form controls of the detail page keep working inside the dialog.
        <Box onClick={event => event.stopPropagation()}>
            <IconButton
                size="small"
                aria-label={formatMessage({
                    id: 'catalog.productDetail.open',
                    defaultMessage: 'Show product details',
                })}
                data-testid={`product-detail-button-${id}`}
                onClick={stopAndRun(() => setDialogOpen(true))}
            >
                <ArticleOutlinedIcon />
            </IconButton>
            {(dialogOpen || configuring) && (
                <ProductDetailSession
                    dialogOpen={dialogOpen}
                    prototypeId={
                        target.hierarchyId === prototypeHierarchyId ? target.nodeId : undefined
                    }
                    onCloseDialog={closeDialog}
                    onConfigure={startConfiguring}
                    onConfiguratorClosed={stopConfiguring}
                >
                    <Stack direction="row" alignItems="center" gap={2} padding={2}>
                        <Tooltip title={closeLabel}>
                            <IconButton
                                aria-label={closeLabel}
                                color="primary"
                                onClick={closeDialog}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                        <Typography variant="h6">{description}</Typography>
                    </Stack>
                    <Box height="75vh" overflow="auto">
                        {isPageNode ? (
                            <CustomPageDetailView page={page} nodeId={target.nodeId} />
                        ) : (
                            <PrototypeModelDetailPageContent
                                page={page}
                                prototypeHierarchyId={target.hierarchyId}
                                prototypeNodeId={target.nodeId}
                            />
                        )}
                    </Box>
                </ProductDetailSession>
            )}
        </Box>
    );
}
